import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';

import { Button } from '@mui/material';
import Async from 'components/Async';
import Form from 'components/Form';
import Modal, { IModalProps } from 'components/Modal';
import GrowTransition from 'components/Modal/animations/grow';
import store from 'store';

import { getMeetLessons } from '@common/api/meets';
import SelectField from '@common/components/Form/SelectField';
import TextField from '@common/components/Form/TextField';

import StyledAppointmentsBookingModalForm from './styled';

interface BookingFormValues {
	day: string;
	time: string;
	format: string;
	program: string;
	phone: string;
}

interface PropsType extends Omit<IModalProps, 'title' | 'onSubmit'> {
	initialValues: any;
	onSubmit: (data: BookingFormValues) => void;
	submitButtonText?: string;
	filters: any;
}

function AppointmentsBookingModal({
	initialValues,
	onSubmit,
	submitButtonText = 'Book',
	filters,
	...props
}: PropsType) {
	const [loading, setLoading] = useState(true);
	const [programs, setPrograms] = useState([]);
	const [lessons, setLessons] = useState([]);

	useEffect(() => {
		if (!props.open) {
			return;
		}
		const loadLessons = async (studentId: number, groupId: number) => {
			try {
				setLoading(true);
				const result = await getMeetLessons(studentId, groupId);
				setPrograms(
					result.programs.map((program: any) => ({
						label: program.program_name,
						value: program.program_id,
					})),
				);
				setLessons(result.lessons);
			} finally {
				setLoading(false);
			}
		};
		loadLessons(store.profile.student?.id, filters.group_id);
	}, [props.open]);

	const handleSubmit = (values: any) => {
		const selectedLesson = lessons.find(
			(lessonToCheck) => lessonToCheck.lesson_id === values.lesson,
		);
		if (!selectedLesson) {
			return;
		}
		return onSubmit({
			...values,
			gradebook_id: selectedLesson.gradebook_id,
			program_group_id: selectedLesson.program_group_id,
		});
	};

	return (
		<Modal
			PaperProps={{
				sx: {
					maxWidth: '500px',
				},
			}}
			title="Booking"
			TransitionComponent={GrowTransition}
			transitionDuration={500}
			{...props}
		>
			<Form initialValues={initialValues} onSave={handleSubmit} hideActions>
				{({ values }) => (
					<StyledAppointmentsBookingModalForm>
						<Async loading={loading}>
							<TextField name="day" label="Day" disabled />
							<TextField name="time" label="Time" disabled />
							<TextField name="phone" label="Phone" />
							<SelectField label="Program" name="program" options={programs} />
							{values.program && (
								<SelectField
									label="Lesson"
									name="lesson"
									options={lessons
										.filter((lesson) => lesson.program_id === values.program)
										.map((lesson) => ({
											value: lesson.lesson_id,
											label: lesson.lesson_name,
										}))}
								/>
							)}
						</Async>
						<Button variant="contained" type="submit">
							{submitButtonText}
						</Button>
					</StyledAppointmentsBookingModalForm>
				)}
			</Form>
		</Modal>
	);
}

export default observer(AppointmentsBookingModal);
